import { BiLeftArrowAlt } from "react-icons/bi";
import { useLocation, useNavigate, Link } from "react-router";
import {
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_BILLING_INFO,
  ROUTE_CLIENTS_CONTACTS,
  ROUTE_CLIENTS_DETAILS,
  ROUTE_CLIENTS_INVOICES,
  ROUTE_CLIENTS_PRICE_EXCEPTIONS,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW_TAB,
  ROUTE_CLIENTS_WORK,
} from "../../SharedModule/utils/constants";
import { TabLabel } from "../../SharedModule/components/TabLabel";
import { Client } from "../types/clients-module-types";
import {
  IsActualClientForHeaderInfoLoading,
  SelectActualClientForHeaderInfo,
} from "../redux/reducers/clientsList.reducer";
import { useSelector } from "react-redux";
import { CheckSkeleton } from "../../SharedModule/utils/checkSkeleton";

export default function HeaderClientsModule({
  showBackButton = true,
  isLoadingPage = false,
  backFromRelationshipPage = false,
}: Readonly<{
  showBackButton?: boolean;
  isLoadingPage?: boolean;
  backFromRelationshipPage?: boolean;
}>) {
  const navigate = useNavigate();
  const location = useLocation();

  const checkActiveTab = (tab: string) => {
    const regex = new RegExp(`${tab}(?:/\\d+)?$`);
    return regex.test(location.pathname);
  };

  const actualClient = useSelector(SelectActualClientForHeaderInfo);
  const isLoadingActualClient = useSelector(IsActualClientForHeaderInfoLoading);

  const onClickHandler = (route: string, client: Client) => {
    let clientId = client.id ?? 0;
    navigate(route.replace(":id", String(clientId)), {
      state: {
        client: client,
        from:
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            : "",
      },
    });
  };

  return (
    <div className="header-module-container">
      {showBackButton && !backFromRelationshipPage && (
        <Link
          className="back-button-container text-decoration-none"
          to={ROUTE_CLIENTS}
        >
          <BiLeftArrowAlt className="back-button-arrow" />
          <span className="back-button-text">Back to Client List</span>
        </Link>
      )}
      {showBackButton && backFromRelationshipPage && (
        <Link
          className="back-button-container text-decoration-none"
          to={ROUTE_CLIENTS_RELATIONSHIP_REVIEW}
        >
          <BiLeftArrowAlt className="back-button-arrow" />
          <span className="back-button-text">
            Back to Relationship Review List
          </span>
        </Link>
      )}
      <CheckSkeleton
        isLoading={isLoadingActualClient}
        style={{ width: "25%", height: "38px" }}
      >
        <div className={`header-module-title ${showBackButton ? "" : "mt-3"}`}>
          {actualClient?.companyName}{" "}
          {actualClient?.isActive ? (
            <span className="bagde active-bagde">Active</span>
          ) : (
            <span className="bagde inactive-bagde">Inactive</span>
          )}
        </div>
      </CheckSkeleton>
      <CheckSkeleton
        isLoading={isLoadingActualClient}
        style={{ width: "25%", height: "20px" }}
      >
        <div className="header-module-subtitle">
          <span className="fw-700">ITX Client ID: </span> {actualClient?.id}
          {actualClient?.companyOwnerName && (
            <span>
              {" "}
              | <span className="fw-700">Client Owner: </span>
              {actualClient?.companyOwnerName}
            </span>
          )}
        </div>
      </CheckSkeleton>
      <div className="d-flex flex-row mt-3">
        <TabLabel
          key="clientInfo"
          active={checkActiveTab("details")}
          classes="top-nav-tab"
          disabled={isLoadingPage}
          onClick={() => onClickHandler(ROUTE_CLIENTS_DETAILS, actualClient)}
        >
          <div
            className={`header-module-link ${isLoadingPage ? "disabled" : ""}`}
          >
            Client Info
          </div>
        </TabLabel>

        <TabLabel
          key="billingInfo"
          active={checkActiveTab("billingInfo")}
          classes="top-nav-tab"
          disabled={isLoadingPage}
          onClick={() =>
            onClickHandler(ROUTE_CLIENTS_BILLING_INFO, actualClient)
          }
        >
          <div
            className={`header-module-link ${isLoadingPage ? "disabled" : ""}`}
          >
            Billing Info
          </div>
        </TabLabel>

        <TabLabel
          key="contacts"
          active={
            checkActiveTab("contacts") || checkActiveTab("contactProfile")
          }
          classes="top-nav-tab"
          disabled={isLoadingPage}
          onClick={() => onClickHandler(ROUTE_CLIENTS_CONTACTS, actualClient)}
        >
          <div
            className={`header-module-link ${isLoadingPage ? "disabled" : ""}`}
          >
            Contacts
          </div>
        </TabLabel>

        <TabLabel
          key="priceExceptions"
          active={checkActiveTab("priceExceptions")}
          classes="top-nav-tab"
          disabled={isLoadingPage}
          onClick={() =>
            onClickHandler(ROUTE_CLIENTS_PRICE_EXCEPTIONS, actualClient)
          }
        >
          <div
            className={`header-module-link ${isLoadingPage ? "disabled" : ""}`}
          >
            Price Exceptions
          </div>
        </TabLabel>

        <TabLabel
          key="work"
          active={checkActiveTab("work")}
          classes="top-nav-tab"
          disabled={isLoadingPage}
          onClick={() => onClickHandler(ROUTE_CLIENTS_WORK, actualClient)}
        >
          <div
            className={`header-module-link ${isLoadingPage ? "disabled" : ""}`}
          >
            Work
          </div>
        </TabLabel>

        <TabLabel
          key="invoices"
          active={checkActiveTab("invoices")}
          classes="top-nav-tab"
          disabled={isLoadingPage}
          onClick={() => onClickHandler(ROUTE_CLIENTS_INVOICES, actualClient)}
        >
          <div
            className={`header-module-link ${isLoadingPage ? "disabled" : ""}`}
          >
            Invoices
          </div>
        </TabLabel>

        <TabLabel
          key="relationshipReviews"
          active={
            checkActiveTab("relationshipReviews") ||
            checkActiveTab("keyContacts") ||
            checkActiveTab("newKeyContact") ||
            checkActiveTab("confidenceHistory")
          }
          classes="top-nav-tab"
          disabled={isLoadingPage}
          onClick={() =>
            onClickHandler(ROUTE_CLIENTS_RELATIONSHIP_REVIEW_TAB, actualClient)
          }
        >
          <div
            className={`header-module-link ${isLoadingPage ? "disabled" : ""}`}
          >
            Relationship Reviews
          </div>
        </TabLabel>
      </div>
      <hr className="header-separator" />
    </div>
  );
}
